import {
  CheckBoxOptionType,
  OptionType,
} from "@portal/BaseComponents/CheckBox/type";
import { MultiSelectElement } from "@portal/BaseComponents/Filters/Elements/MultiSelect/MultiSelectElement";
import { FilterDropdownDown } from "@portal/BaseComponents/Filters/FilterDropdown/FilterDropdownDown";
import { HideWrapper } from "@portal/BaseComponents/HideWrapper/HideWrapper";
import useOpen from "@portal/hooks/useOpen";
import { paymentFiltersAtom } from "@portal/store/Filters/payments/payments-filter";
import { resetPage, tableAtom } from "@portal/store/table/table";
import { InvoiceStatus } from "@portal/types/entities/invoice";
import { PaymentStatus, paymentStatus } from "@portal/types/entities/payment";
import produce from "immer";
import { useAtom, useSetAtom } from "jotai";
import { useMemo } from "react";
import { getPaymentStatus } from "../../predicate";

const optionLabelPredicate = (option: PaymentStatus) =>
  getPaymentStatus(option).label;
const optionsPredicate = (
  option: PaymentStatus
): CheckBoxOptionType<PaymentStatus> => {
  return {
    label: getPaymentStatus(option).label,
    value: option,
  };
};

const paymentStatusOptions = paymentStatus
  .filter((f) => f !== "APPLIED")
  .map(optionsPredicate);

function PaymentStatusFilter() {
  const { onClose, open, toggleOpen, handleOpenChange } = useOpen({
    open: false,
  });
  const setTable = useSetAtom(tableAtom);
  const [paymentFilters, setPaymentFilters] = useAtom(paymentFiltersAtom);
  function handleHideReset() {
    return !paymentFilters.filterValues.statuses?.length;
  }
  function getSelectedFilterValues() {
    return paymentFilters.filterValues.statuses?.length
      ? paymentFilters.filterValues.statuses.length ===
        paymentStatusOptions.length
        ? "All"
        : paymentFilters.filterValues.statuses
            .map(optionLabelPredicate)
            .join(", ")
      : "All";
  }

  function handleReset() {
    setPaymentFilters((filter) => {
      return produce(filter, (draft) => {
        draft.filterValues.statuses = [];
        return draft;
      });
    });
    setTable(resetPage);
    onClose();
  }

  function handleSubmit(value: OptionType["value"][]) {
    setPaymentFilters((filter) => {
      return produce(filter, (draft) => {
        draft.filterValues.statuses = value as PaymentStatus[];
        return draft;
      });
    });
    setTable(resetPage);
    onClose();
  }

  const Content = (
    <MultiSelectElement<InvoiceStatus>
      multiSelectProps={{
        initialValue: paymentFilters.filterValues.statuses,
        options: paymentStatusOptions ?? [],
        onSubmit: handleSubmit,
        onCancel: onClose,
      }}
    />
  );

  const selectedValues = useMemo(getSelectedFilterValues, [
    paymentFilters.filterValues.statuses,
  ]);
  const hideReset = handleHideReset();
  return (
    <HideWrapper hide={!paymentFilters.activeFilterKeys.includes("statuses")}>
      <FilterDropdownDown
        filterBlockProps={{
          isDefault: true,
          toggleOpen,
          open,
          title: "Payment Status",
          filterValues: selectedValues,
          onReset: handleReset,
          hideReset,
        }}
        filterComponent={Content}
        filterTitle={"Payment Status"}
        onopenChange={handleOpenChange}
      />
    </HideWrapper>
  );
}

export { PaymentStatusFilter };
